import { atom } from 'jotai';
import { atomWithReducer, atomWithReset } from 'jotai/utils';

import type { TabRoute } from '@/components/inbox/types';

export const tabSelectedKeyAtom = atomWithReset<TabRoute['key']>('open');

/**
 * Inquiry Close State Machine
 *
 * Flow:
 * 1. Confirmation
 * 2. Close
 * 3. Feedback
 * 4. Navigation
 *
 * Status progression:
 * 1. idle
 * 2. confirming
 * 3. closing
 * 4. awaitingFeedback
 * 5. complete
 *
 * NavigationTarget is captured at close time to determine post-feedback
 * navigation, avoiding re-evaluation issues on mobile.
 */
export type InquiryCloseStatus =
  | 'idle'
  | 'confirming'
  | 'closing'
  | 'awaitingFeedback'
  | 'complete';

export type NavigationTarget = {
  shouldNavigate: boolean;
  nextConversationId: string | null;
};

export type InquiryCloseState = {
  status: InquiryCloseStatus;
  conversationId: string | null;
  navigationTarget: NavigationTarget | null;
  startedAt: number | null;
};

export type InquiryCloseAction =
  | { type: 'INITIATE'; conversationId: string }
  | { type: 'CONFIRM' }
  | { type: 'SET_NAVIGATION_TARGET'; target: NavigationTarget }
  | { type: 'START_FEEDBACK' }
  | { type: 'COMPLETE_FEEDBACK' }
  | { type: 'RESET' };

const INITIAL_STATE: InquiryCloseState = {
  status: 'idle',
  conversationId: null,
  navigationTarget: null,
  startedAt: null,
};

function inquiryCloseReducer(
  state: InquiryCloseState,
  action: InquiryCloseAction
): InquiryCloseState {
  switch (action.type) {
    case 'INITIATE':
      if (state.status !== 'idle') {
        return state;
      }

      return {
        status: 'confirming',
        conversationId: action.conversationId,
        navigationTarget: null,
        startedAt: Date.now(),
      };

    case 'CONFIRM':
      if (state.status !== 'confirming') {
        return state;
      }

      return { ...state, status: 'closing' };

    case 'SET_NAVIGATION_TARGET':
      if (state.status !== 'closing' || state.navigationTarget !== null) {
        return state;
      }

      return { ...state, navigationTarget: action.target };

    case 'START_FEEDBACK':
      if (state.status !== 'closing') {
        return state;
      }

      return { ...state, status: 'awaitingFeedback' };

    case 'COMPLETE_FEEDBACK':
      if (state.status !== 'awaitingFeedback') {
        return state;
      }

      return { ...state, status: 'complete' };

    case 'RESET':
      return INITIAL_STATE;

    default:
      return action satisfies never;
  }
}

export const inquiryCloseAtom = atomWithReducer<InquiryCloseState, InquiryCloseAction>(
  INITIAL_STATE,
  inquiryCloseReducer
);

export const isFeedbackActiveAtom = atom((get) => {
  const { status } = get(inquiryCloseAtom);

  return status === 'closing' || status === 'awaitingFeedback';
});
